/**
 * Side panel: KPIs grouped by tag, plus plan / columns / color key.
 * Each tag expands or collapses and can select or clear every KPI on that tag.
 */

import React, { useMemo, useState } from 'react';
import { ColorKey } from './ColorKey';
import { ColumnsControls } from './ColumnsControls';
import { CompareControls } from './CompareControls';
import { CompositeEditor } from './CompositeEditor';
import { SortControls } from './SortControls';
import { KpiTagGroup } from './KpiTagGroup';
import { groupCatalogByTag, UNTAGGED_TAG_KEY } from './groupCatalogByTag';
import {
  GUIDED_TAB_KPI_MAX,
  type CatalogKpi,
  type CompareOptions,
  type ComparePrefs,
  type CompositeCard,
  type CompositeDef,
  type KpiRowColumns,
  type TileSort
} from './types';

interface KpiPickerPanelProps {
  catalog: CatalogKpi[];
  /** Hidden ids (hide mode) or selected ids (include mode). */
  ids: string[];
  mode: 'hide' | 'include';
  saving: boolean;
  compare: ComparePrefs;
  options: CompareOptions;
  columns: number;
  onChange: (ids: string[]) => void;
  onCompareChange: (compare: ComparePrefs) => void;
  onColumnsPreview: (columns: KpiRowColumns) => void;
  onColumnsChange: (columns: KpiRowColumns) => void;
  sort: TileSort;
  onSortChange: (sort: TileSort) => void;
  tab: string;
  composites: CompositeCard[];
  onCompositesChange: (defs: CompositeDef[]) => void;
  thresholds?: { yellowOffPct: number; redOffPct: number };
  t: (key: string) => string;
}

const ROLE_LABEL: Record<string, string> = {
  ceo: 'kpi.perfGroupCeo',
  cfo: 'kpi.perfGroupCfo',
  coo: 'kpi.perfGroupCoo',
  cio: 'kpi.perfGroupCio'
};

export const KpiPickerPanel: React.FC<KpiPickerPanelProps> = ({
  catalog,
  ids,
  mode,
  saving,
  compare,
  options,
  columns,
  onChange,
  onCompareChange,
  onColumnsPreview,
  onColumnsChange,
  sort,
  onSortChange,
  tab,
  composites,
  onCompositesChange,
  thresholds,
  t
}) => {
  const stored = useMemo(() => new Set(ids), [ids]);
  const checked = useMemo(() => {
    if (mode === 'include') return stored;
    return new Set(catalog.filter((item) => !stored.has(item.kpiId)).map((item) => item.kpiId));
  }, [mode, stored, catalog]);

  const groups = useMemo(() => groupCatalogByTag(catalog), [catalog]);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const emit = (nextChecked: Set<string>) => {
    if (mode === 'include') {
      onChange(Array.from(nextChecked));
      return;
    }
    onChange(catalog.filter((item) => !nextChecked.has(item.kpiId)).map((item) => item.kpiId));
  };

  const toggleOne = (kpiId: string) => {
    const next = new Set(checked);
    if (next.has(kpiId)) next.delete(kpiId);
    else next.add(kpiId);
    emit(next);
  };

  const toggleTag = (kpiIds: string[], selectAll: boolean) => {
    const next = new Set(checked);
    for (const kpiId of kpiIds) {
      if (selectAll) next.add(kpiId);
      else next.delete(kpiId);
    }
    emit(next);
  };

  const selectAll = () => emit(new Set(catalog.map((item) => item.kpiId)));
  const selectNone = () => emit(new Set());
  const expandAll = () => setOpen(Object.fromEntries(groups.map((group) => [group.key, true])));
  const collapseAll = () => setOpen(Object.fromEntries(groups.map((group) => [group.key, false])));

  const tagLabel = (key: string, tag: string) => {
    if (key === UNTAGGED_TAG_KEY) return t('kpi.perfTagUntagged') || 'Untagged';
    const roleKey = ROLE_LABEL[key];
    if (roleKey) return t(roleKey) || tag;
    return tag;
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-1 mb-2">
        <h2 className="text-xs font-medium text-[var(--color-surface-text)]">
          {t('kpi.perfPicker') || 'KPIs'}
        </h2>
        <div className="flex flex-wrap justify-end gap-x-1">
          <button
            type="button"
            disabled={saving || catalog.length === 0}
            className="text-[10px] text-[var(--color-secondary-text)] hover:underline disabled:opacity-50"
            onClick={selectAll}
          >
            {t('kpi.perfSelectAll') || 'All'}
          </button>
          <span className="text-[10px] text-[var(--color-border)]">|</span>
          <button
            type="button"
            disabled={saving || catalog.length === 0}
            className="text-[10px] text-[var(--color-secondary-text)] hover:underline disabled:opacity-50"
            onClick={selectNone}
          >
            {t('kpi.perfSelectNone') || 'None'}
          </button>
          <span className="text-[10px] text-[var(--color-border)]">|</span>
          <button
            type="button"
            disabled={groups.length === 0}
            className="text-[10px] text-[var(--color-secondary-text)] hover:underline disabled:opacity-50"
            onClick={expandAll}
          >
            {t('kpi.perfExpandAllTags') || 'Expand all'}
          </button>
          <span className="text-[10px] text-[var(--color-border)]">|</span>
          <button
            type="button"
            disabled={groups.length === 0}
            className="text-[10px] text-[var(--color-secondary-text)] hover:underline disabled:opacity-50"
            onClick={collapseAll}
          >
            {t('kpi.perfCollapseAllTags') || 'Collapse all'}
          </button>
        </div>
      </div>

      <CompareControls
        compare={compare}
        options={options}
        saving={saving}
        onChange={onCompareChange}
        t={t}
      />

      <ColumnsControls
        columns={columns}
        saving={saving}
        onPreview={onColumnsPreview}
        onCommit={onColumnsChange}
        t={t}
      />

      <SortControls sort={sort} saving={saving} onChange={onSortChange} t={t} />

      {ids.length > GUIDED_TAB_KPI_MAX && (
        <p className="text-[10px] text-[var(--color-secondary-text)] mb-2 leading-tight">
          {t('kpi.perfTabKpiGuide') ||
            'Keep about 8 KPIs on a tab. Add another tab for another view.'}
        </p>
      )}

      <CompositeEditor
        tab={tab}
        catalog={catalog}
        cards={composites}
        saving={saving}
        onSave={onCompositesChange}
        t={t}
      />

      {catalog.length === 0 ? (
        <p className="text-[10px] text-[var(--color-secondary-text)] mb-2">
          {t('kpi.perfNoPickerKpis') || 'No KPIs on this tab.'}
        </p>
      ) : (
        <ul className="space-y-1 max-h-[22rem] overflow-y-auto mb-3 pr-0.5">
          {groups.map((group) => (
            <KpiTagGroup
              key={group.key}
              label={tagLabel(group.key, group.tag)}
              items={group.items}
              checkedIds={checked}
              expanded={open[group.key] === true}
              saving={saving}
              onToggleExpand={() =>
                setOpen((current) => ({ ...current, [group.key]: current[group.key] !== true }))
              }
              onToggleTag={(select) => toggleTag(group.items.map((item) => item.kpiId), select)}
              onToggleOne={toggleOne}
              expandLabel={t('kpi.perfExpandTag') || 'Show KPIs'}
              collapseLabel={t('kpi.perfCollapseTag') || 'Hide KPIs'}
              selectLabel={t('kpi.perfSelectTag') || 'Select all KPIs with this tag'}
            />
          ))}
        </ul>
      )}

      <div className="pt-2 border-t border-[var(--color-border)]">
        <ColorKey t={t} thresholds={thresholds} />
      </div>
    </div>
  );
};
